import type { TenantMembership } from './actingTenant';

/**
 * EAA-1694: read the caller's tenant memberships from ACH `GET /api/v1/me/access`.
 *
 * D10 — the SSO token carries identity claims ONLY, no roles and no tenants. Memberships
 * and effective permissions are always computed server-side from the caller's provisioned
 * memberships, which is why this is a network read and not a token decode. Anything that
 * derived a tenant or a permission from the token itself would be reading a claim the
 * contract guarantees is absent.
 *
 * Called with an explicit bearer rather than through the shared API client: it runs before
 * an acting tenant exists, and `/me/access` is the one endpoint that must work in exactly
 * that state (it is what tells the app which tenants there are to choose from).
 */
export interface MeAccess {
    user: { sub: string; email?: string | null; name?: string | null };
    memberships: TenantMembership[];
}

interface MeAccessEnvelope {
    data?: {
        user?: { sub?: string; email?: string | null; name?: string | null };
        tenants?: Array<{
            tenant_id?: string;
            slug?: string | null;
            name?: string | null;
            permissions?: string[];
        }>;
    };
}

/** `${VITE_API_URL}` is `.../api`; `/me/access` is only mounted under the versioned prefix. */
function meAccessUrl(apiBaseUrl: string): string {
    const base = apiBaseUrl.replace(/\/+$/, '');
    return base.endsWith('/v1') ? `${base}/me/access` : `${base}/v1/me/access`;
}

export async function fetchMeAccess(apiBaseUrl: string, accessToken: string): Promise<MeAccess> {
    const resp = await fetch(meAccessUrl(apiBaseUrl), {
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
    });
    if (!resp.ok) {
        throw new Error(`me/access failed with HTTP ${resp.status}`);
    }
    const body = (await resp.json()) as MeAccessEnvelope;
    const tenants = body.data?.tenants ?? [];
    return {
        user: {
            sub: body.data?.user?.sub ?? '',
            email: body.data?.user?.email ?? null,
            name: body.data?.user?.name ?? null,
        },
        // A membership with no tenant_id is unusable; drop it rather than carrying a
        // half-formed entry into the tenant picker.
        memberships: tenants
            .filter((t): t is { tenant_id: string } & typeof t => Boolean(t.tenant_id))
            .map((t) => ({
                tenantId: t.tenant_id,
                slug: t.slug ?? null,
                name: t.name ?? null,
                permissions: t.permissions ?? [],
            })),
    };
}
